import { createAccount, createAccountComplete } from '@/scripts/datalayer'

class AccountForm extends HTMLElement {
  constructor() {
    super()

    this.form = this.querySelector('form')
    this.result = this.querySelector('[data-result]')
    this.subtext = document.querySelector('.js-subtext')

    if (this.form) {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this))
    }

    switch (this.getAttribute('action')) {
      case 'resetPassword':
        // Retrieve access token on load for "reset password" form
        this.getAccessToken()
        break
      case 'confirmEmail':
        // Perform request to confirm email on load for "confirm email" (activate account) view
        this.confirmEmail()
        break
    }
  }

  // Toggle submitting state on form button (disabled while submitting)
  toggleSubmitting(isSubmitting = true) {
    const button = this.querySelector('button[type="submit"]')

    if (isSubmitting) {
      button.setAttribute('disabled', true)
    } else {
      button.removeAttribute('disabled')
    }
  }

  // Get form values as JS object
  get values() {
    return Object.fromEntries(new FormData(this.form))
  }

  // Get auth code for access token request from query param
  get authCode() {
    const params = new URLSearchParams(window.location.search)
    let code = params.get('code');
    if (code && code.indexOf('__') > 0) {
      code = code.slice(0, code.indexOf('__'));
    }

    return code;
  }

  // Get verification code for confirm email request from query param
  get verificationCode() {
    const params = new URLSearchParams(window.location.search)
    let code = params.get('verification_code');
    if (code && code.indexOf('__') > 0) {
      return code.substring(0, code.indexOf('__'));
    }

    return code;
  }

  // Perform request to get access token
  getAccessToken() {
    const code = this.authCode;

    if (!code) {
      // Show error message
      return this.showError(true)
    }

    let status
    fetch(`${window.BARREL.sbdApiUrl}/aic/getAccessToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        token: window.BARREL.sbdApiAccessToken,
      },
      body: JSON.stringify({ code }),
    }).then((response) => {
      status = response.status
      return response.json()
    }).then((responseJson) => {
      if (status === 200) {
        this._accessToken = responseJson.access_token
      } else {
        // Show error message
        this.showError(true)
      }
    })
  }

  // Perform request to confirm email
  confirmEmail() {
    fetch(`${window.BARREL.sbdApiUrl}/aic/confirmEmail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        token: window.BARREL.sbdApiAccessToken,
      },
      body: JSON.stringify({ verificationCode: this.verificationCode }),
    }).then((response) => {
      if (response.status === 200) {
        createAccountComplete()
        this.showSuccess(true)
      } else {
        this.showError(true)
      }
    })
  }

  // Submit data from form to API endpoint
  onFormSubmit(event) {
    event.preventDefault()

    const action = this.getAttribute('action')

    const locale = navigator.languages && navigator.languages.length
        ? navigator.languages[0]
        : navigator.language;

    // Create data payload including locale and form values
    const data = {
      locale,
      ...this.values
    }

    // Add access token if present
    if (this._accessToken) {
      data.accessToken = this._accessToken
    }

    // Add leadSource field
    if (action === 'login' || action === 'register') {
      data.leadSource = 'website_registration'
    }

    if (action === 'register') {
      // Add additional account details for "register" requests
      data.details = {
        "address1": "",
        "address2": "",
        "city": "",
        "province": "",
        "country": "",
        "zip": "",
        "province_code": "",
        "country_code": "",
        "country_name": "",
        "phone": "",
        "birth_date": "",
        "gender": "",
        "accepts_marketing": !!data.acceptsMarketing,
        "preferences": ""
      }
      delete data.acceptsMarketing
    }

    this.toggleSubmitting(true)

    try {
      // Perform API request for form submission
      return fetch(`${window.BARREL.sbdApiUrl}/aic/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: window.BARREL.sbdApiAccessToken,
        },
        body: JSON.stringify(data),
      }).then((response) => {
        // Handle API response using callbacks defined at top of file
        switch (action) {
          case 'register':
            return this.handleRegisterResponse(response)
          case 'login':
            return this.handleLoginResponse(response)
          case 'requestResetPassword':
            return this.handleRequestResetPasswordResponse(response)
          case 'resetPassword':
            return this.handleResetPasswordResponse(response)
          case 'sendVerificationEmail':
            return this.handleSendVerificationEmailResponse(response)
        }
      }).then(({
        success,
        error,
        invalidCredentials,
        invalidFields,
        // code,
        emailVerified
      }) => {
        this.toggleSubmitting(false)

        if (invalidCredentials) {
          this.showInvalidCredentialsErrors()
        } else if (invalidFields) {
          this.showInvalidFieldErrors(invalidFields)
        } else if (typeof success !== 'undefined') {
          this.showSuccess(success)
        } else if (typeof error !== 'undefined') {
          this.showError(error)
        }

        if (action === 'login') {
          if (emailVerified !== undefined && !emailVerified) {
            this.showError('')
            this.showResendVerificationEmail(true)
          }
          else {
            this.showResendVerificationEmail(false)
          }
        }

        if (action === 'sendVerificationEmail') {
          this.subtext.classList.add('hidden')
        }
      })
    } catch(e) {
      // Show generic error message on failed request
      this.showError(true)
    }
  }

  async handleRegisterResponse (response) {
    if (response.status === 200) {
      // Show success message
      createAccount()
      return { success: true }
    } else if (response.status === 400) {
      const responseJson = await response.json()

      if (responseJson.data?.invalid_fields) {
        // Show errors on invalid fields
        return { invalidFields: responseJson.data.invalid_fields }
      }

      if (responseJson.message) {
        // Show error message from response
        return { error: responseJson.message }
      }
    }

    // Show generic error message
    return { error: true }
  }

  async handleLoginResponse (response) {
    if (response.status == 200) {
      // Redirect to URL from API response to complete login flow
      const responseText = await response.text()
      if (responseText) {
        localStorage.setItem('isRedirect', 'true')
        window.location.href = responseText
        return
      }
    } else if (response.status == 400) {
      const responseJson = await response.json()

      let errorMessage
      let invalidFields
      let invalidCredentials

      if (responseJson.data?.error === "invalid_credentials"){
        invalidCredentials = true
      } else if (responseJson.data?.invalid_fields) {
        if (responseJson.data.invalid_fields.signInForm) {
          errorMessage = responseJson.data.invalid_fields.signInForm[0]
          delete responseJson.data.invalid_fields.signInForm
        }
        if (Object.keys(responseJson.data.invalid_fields).length) {
          invalidFields = responseJson.data.invalid_fields
        }
      }

      if (!errorMessage) {
        errorMessage = responseJson.message
      }

      if (invalidCredentials) {
        return {  invalidCredentials }
      }
      else if (invalidFields) {
        // Show errors on invalid fields
        return { invalidFields }
      } else {
        // Show error message from response
        return { error: errorMessage, emailVerified: responseJson.emailVerified }
      }
    }

    // Show generic error message
    return { error: true }
  }

  async handleRequestResetPasswordResponse (response) {
    if (response.status == 200) {
      // Show success message
      return { success: true }
    } else if (response.status == 400) {
      const responseJson = await response.json()
      // const errorMessages = responseJson.data?.invalid_fields?.forgotPasswordForm
      //  return { error: errorMessages?.length ? errorMessages[0] : true }
      const errorMessages = `If an account with that email is found, a reset password link will be emailed to you.`

      // Show error message from response or generic error message
     return { error: errorMessages?.length ? errorMessages : true }
    }
  }

  async handleResetPasswordResponse (response) {
    if (response.status == 200) {
      // Show success message
      return { success: true }
    } else if (response.status === 400) {

      const responseJson = await response.json()

      let errorMessage
      let invalidFields
      let invalidCredentials

      if (responseJson.data?.error === "invalid_credentials"){
        invalidCredentials = true
      } else if (responseJson.data?.invalid_fields) {
        // if (responseJson.data.invalid_fields.newPassword) {
        //   errorMessage = responseJson.data.invalid_fields.newPassword[0]
        //   delete responseJson.data.invalid_fields.newPassword
        // }
        if (Object.keys(responseJson.data.invalid_fields).length) {
          invalidFields = responseJson.data.invalid_fields
        }
      }

      if (!errorMessage) {
        errorMessage = responseJson.message
      }

      if (invalidCredentials) {
        return {  invalidCredentials }
      }
      else if (invalidFields) {
        // Show errors on invalid fields
        return { invalidFields }
      } else {
        // Show error message from response
        return { error: errorMessage, emailVerified: responseJson.emailVerified }
      }
    }

    // Show generic error message
    return { error: true }
  }

  async handleSendVerificationEmailResponse (response) {
    if (response.status == 200) {
      // Show success message
      return { success: true }
    } else if (response.status === 400) {
      const responseJson = await response.json()

      // Show error message from response
      return { error: responseJson.message, code: responseJson.data?.code }
    }

    // Show generic error message
    return { error: true }
  }

  showSuccess(success) {
    this.result.innerHTML = typeof success === 'string' ? success : this.dataset.successMessage
    this.subtext?.classList.add('hidden')
    this.form?.classList.add('hidden')
    this.result.classList.remove('hidden')
  }

  showError(error) {
    this.result.innerHTML = typeof error === 'string' ? error : this.dataset.errorMessage
    if (this.getAttribute('action') != 'login') {
      this.form?.classList.add('hidden')
    }
    this.result.classList.remove('hidden')

    const invalidCredentials = this.querySelector('[invalid-credentials]')
    invalidCredentials.classList.add('hidden')
  }

  showInvalidCredentialsErrors() {
    const invalidCredentials = this.querySelector('[invalid-credentials]')

    this.result.classList.add('hidden')
    invalidCredentials.classList.remove('hidden')
  }

  showInvalidFieldErrors(invalidFields) {
    invalidFields.email = invalidFields.emailAddress
    delete invalidFields.emailAddress

    invalidFields.password = invalidFields.newPassword
    delete invalidFields.newPassword

    invalidFields.passwordConfirm = invalidFields.newPasswordConfirm
    delete invalidFields.newPasswordConfirm

    this.querySelectorAll('text-input').forEach((textInput) => {
      const inputEl = textInput.querySelector('input')
      if (invalidFields[inputEl.name]) {
        textInput.showError(invalidFields[inputEl.name][0])
      }else{
        // clear errors
        if(textInput.classList.contains("has-error")){
          textInput.classList.remove("has-error")
          textInput.querySelector("[data-error]").innerText = ''
        }
      }
    })
  }

  showResendVerificationEmail(show){
    const resendVerificationEmail = this.querySelector('[resend-verification-email]')

    if (show) {
      this.result.classList.add('hidden')
      resendVerificationEmail.classList.remove('hidden')
    }
    else {
      resendVerificationEmail.classList.add('hidden')
    }
  }
}

customElements.define('account-form', AccountForm)

class AccountRecoverLink extends HTMLElement {
  constructor () {
    super()

    const form = this.closest('[data-form-container]')

    const anchorLink = this.querySelector('a')
    const anchorLinkHash = anchorLink.href.slice(anchorLink.href.indexOf('#'))

    anchorLink.addEventListener('click', (event) => {
      event.preventDefault()
      window.history.replaceState(null, null, `${window.location.pathname}${anchorLinkHash}`)
      form.classList.add('hidden')
      form.nextElementSibling.classList.remove('hidden')
    })

    if (window.location.hash == anchorLinkHash) {
      anchorLink.click()
    }
  }
}

customElements.define('account-recover-link', AccountRecoverLink)

class AccountRecoverCancelLink extends HTMLElement {
  constructor () {
    super()

    const form = this.closest('[data-form-container]')

    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault()
      window.history.replaceState(null, null, window.location.pathname)
      form.classList.add('hidden')
      form.previousElementSibling.classList.remove('hidden')
    })
  }
}

customElements.define('account-recover-cancel-link', AccountRecoverCancelLink)
